import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { IconButton, Tooltip, useAlert, useDialog } from "@digital-services/ui";

import { OrganizationUser } from "@organization/models/OrganizationUser";
import { useInviteOrganizationUser } from "@users/organizationuser/services/OrganizationUserService";

import { useOrganizationContext } from "@hooks/useOrganizationContext";
import RefreshIcon from "@icons/refresh_black_16.svg?react";
import { defaultTriggerDialogOptions } from "@utils/common";

interface Props {
  user: OrganizationUser;
}

export const ReInviteUser: FunctionComponent<Props> = props => {
  const { user } = props;

  const { showDialog } = useDialog();
  const { notifyError, notifySuccess } = useAlert();

  const { t } = useTranslation();
  const { t: tOrg } = useTranslation(undefined, { keyPrefix: "organization" });
  const { organization } = useOrganizationContext();
  const { invite: inviteOrganizationUser } = useInviteOrganizationUser();

  const reInviteOrganizationUser = async () => {
    const apiResponse = await inviteOrganizationUser({
      displayName: user.displayName,
      emailAddress: user.emailAddress,
      organizationRole: user.organizationRole
    });

    const userData = {
      user: user.displayName,
      organization: organization.name
    };

    if (apiResponse.isSuccess) {
      notifySuccess({
        header: tOrg("users.re-invite.success.message", userData),
        "data-testid": "re-invite-user-success"
      });
    } else {
      notifyError({
        header: tOrg("users.re-invite.error.message", {
          ...userData,
          reason: apiResponse.errorMessage
        }),
        "data-testid": "re-invite-user-error"
      });
    }
  };

  const handleClick = async () => {
    showDialog({
      variant: "warning",
      header: tOrg("users.re-invite.confirm.title"),
      content: tOrg("users.re-invite.confirm.message", {
        user: user.displayName,
        organization: organization.name
      }),
      confirmButton: t("button.confirm.label"),
      onConfirm: async () => {
        await reInviteOrganizationUser();
      },
      cancelButton: t("button.cancel.label"),
      "data-testid": "re-invite-organization-user-confirmation",
      ...defaultTriggerDialogOptions
    });
  };

  return (
    <Tooltip message={tOrg("users.re-invite.label")}>
      <IconButton
        onClick={handleClick}
        data-testid={`re-invite-organization-user-${user.id}`}
      >
        <RefreshIcon />
      </IconButton>
    </Tooltip>
  );
};
